// Entry point. When config.USE_MOCKS is true, start the MSW REST mock layer and
// wire the mock WebSocket implementation into the live transports BEFORE the app
// mounts, so the dashboard runs end-to-end without a backend.

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { config } from './config';
import { setSessionToken } from './api/client';
import { setWebSocketImpl } from './transport/wsImpl';
import './styles.css';

async function bootstrap() {
  // Honour a bearer token injected by the embedding host (e.g. the Tauri GUI's
  // initialization script sets window.__MCPGRAPH__.BEARER). This lets the
  // embedded dashboard authenticate to the real gateway without cross-origin
  // cookies. A normal login still overrides this via setSessionToken().
  if (config.BEARER) setSessionToken(config.BEARER);
  // The whole mock layer sits behind the build-time constant __USE_MOCKS__ (vite `define`). In a
  // normal production build it is `false`, so this branch — and the dynamic import() it guards — is
  // dead-code-eliminated: the MSW worker, handlers and fixtures never reach dist (H29/H30). Only a
  // `VITE_USE_MOCKS=1` build keeps them, and even then they load lazily from a separate chunk and
  // still require the runtime config.USE_MOCKS opt-in.
  if (__USE_MOCKS__ && config.USE_MOCKS) {
    const { startMocks, MockWebSocketImpl } = await import('./mocks/browser');
    setWebSocketImpl(MockWebSocketImpl);
    await startMocks();
  }
  const root = document.getElementById('root');
  if (!root) throw new Error('#root not found');
  createRoot(root).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

void bootstrap();
